import type {CSSProperties, ReactNode} from 'react';

import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {cn} from '../../helpers/cn';

export type CollapsibleBottomSheetProps = {
  show: boolean;
  onClose?: () => void;
  /** Sync expanded/collapsed state — used for toggle and swipe gestures */
  onOpenChange?: (open: boolean) => void;
  canceledOnTouchOutside?: boolean;
  children?: ReactNode;
  contentStyle?: CSSProperties;
  /** Expanded height in pixels */
  height?: number;
  /** Peek height when collapsed (default 40) */
  collapsedHeight?: number;
  /** Show drag handle bar */
  showDragHandle?: boolean;
  className?: string;
};

const BOTTOM_SHEET_SWIPE_THRESHOLD = 100;
const DEFAULT_COLLAPSED_HEIGHT = 40;

export function CollapsibleBottomSheet({
  show,
  onClose,
  onOpenChange,
  canceledOnTouchOutside = false,
  children,
  contentStyle,
  height = 400,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  showDragHandle = true,
  className
}: CollapsibleBottomSheetProps) {
  const collapsedOffset = height - collapsedHeight;
  const startY = useRef(0);
  const isDragging = useRef(false);
  const lastSwipeAt = useRef(0);
  const [translateY, setTranslateY] = useState(show ? 0 : collapsedOffset);

  useEffect(() => {
    if (!isDragging.current) {
      setTranslateY(show ? 0 : collapsedOffset);
    }
  }, [show, collapsedOffset]);

  const setOpen = (open: boolean) => {
    onOpenChange?.(open);
    if (!open) {
      onClose?.();
    }
  };

  const handleToggle = () => {
    if (Date.now() - lastSwipeAt.current < 500) {
      return;
    }
    setOpen(!show);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    isDragging.current = true;
    startY.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!isDragging.current) {
      return;
    }
    const diff = startY.current - event.touches[0].clientY;
    const startOffset = show ? 0 : collapsedOffset;
    const nextY = Math.max(0, Math.min(collapsedOffset, startOffset - diff));
    setTranslateY(nextY);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (!isDragging.current) {
      return;
    }
    isDragging.current = false;
    const diff = startY.current - event.changedTouches[0].clientY;

    if (diff > BOTTOM_SHEET_SWIPE_THRESHOLD) {
      lastSwipeAt.current = Date.now();
      setOpen(true);
      setTranslateY(0);
      return;
    }

    if (diff < -BOTTOM_SHEET_SWIPE_THRESHOLD) {
      lastSwipeAt.current = Date.now();
      setOpen(false);
      setTranslateY(collapsedOffset);
      return;
    }

    setTranslateY(show ? 0 : collapsedOffset);
  };

  const sheet = (
    <div
      className={cn(
        'pointer-events-none fixed inset-0 z-modal mx-auto flex max-w-mobile items-end justify-center',
        className
      )}
    >
      {show && canceledOnTouchOutside ? (
        <div
          className="pointer-events-auto absolute inset-0 bg-black/40 transition-opacity duration-300"
          onClick={() => setOpen(false)}
          role="presentation"
        />
      ) : null}

      <div
        className="pointer-events-auto flex w-full max-w-125 flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out"
        style={{
          height,
          transform: `translateY(${translateY}px)`,
          ...contentStyle
        }}
        role="dialog"
        aria-modal="false"
        aria-expanded={show}
      >
        {showDragHandle ? (
          <button
            type="button"
            className="flex h-10 w-full touch-none cursor-grab items-center justify-center border-0 bg-transparent p-0 active:cursor-grabbing"
            onClick={handleToggle}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            aria-label={show ? 'جمع کردن' : 'باز کردن'}
          >
            <span className="h-1.25 w-12.5 rounded-full bg-gray-04" />
          </button>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-4">{children}</div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
